<template>
    <ValidationObserver
        ref="fileImportValidation"
        v-slot="{ passes }"
    >
    <BaseDialog
        :showDialog="showFileImportDialog"
        maxDialogWidth="1000"
        maxCardHeight="700"
        headline="lang_GENERAL_FILE_IMPORT_DIALOG_TITLE_UPLOAD_FILE"
        :close="closeDialog"
        persistent
        :alvqa="alvqaContext + '-' + alvqa"
    >
        <template v-slot:headerActions>
            <span v-if="$route.name === 'Works' && selectedFileImportOption === 'mappingImport'" class="mt-3">
                <ContextHelpIcon :context="'PublishingWorksListpageImportMapping'" :alvqa="alvqaContext + '-' + alvqa + '-context-help-icon'" />
            </span>
            <span v-if="$route.name === 'Works' && selectedFileImportOption === 'cwr'" class="mt-3">
                <ContextHelpIcon :context="'PublishingWorksCwrImport'" :alvqa="alvqaContext + '-' + alvqa + '-context-help-icon'" />
            </span>
        </template>
<!-- Dialog Text -->
        <template v-slot:dialogText>
            <v-container fluid> 
                <v-row class="mb-3">
                    <v-col 
                        :class="dialogTextClass"
                        v-html="dialogText"
                        :alvqa="alvqaContext + '-' + alvqa + '-text'"
                    ></v-col>
                </v-row>
                <v-row v-if="CsvReportPath !== null">
                    <v-col>
                        <BaseIcon 
                            @click.stop="downloadFile(CsvReportPath)" 
                            icon="cloud_download" 
                            :tooltipText="$t('lang_GENERIC_CAPTION_DOWNLOAD_CSV_REPORT')" 
                            :alvqa="alvqa + '-icon-download-csv-report'" 
                        />
                    </v-col>
                </v-row>

<!-- Special Options for the Upload -->
                <slot 
                    v-if="showUploadOptions" 
                    name="importOptions"
                ></slot>

<!-- Progressbars -->
                <v-row v-if="showUploadProgressbar">
                    <v-col>
                        <v-progress-linear 
                            v-model="uploadProgressbarValue"
                            height="25"
                            color="dialogProgressbar"
                            :alvqa="alvqaContext + '-' + alvqa + '-upload-progressbar'"
                        ></v-progress-linear>
                    </v-col>
                </v-row>
                <v-row v-if="showProcessProgressbar">
                    <v-col>
                        <v-progress-linear
                            v-model="processValue"
                            height="25"
                            color="dialogProgressbar"
                            :alvqa="alvqaContext + '-' + alvqa + '-process-progressbar'"
                        >{{ processValue }} %</v-progress-linear>
                    </v-col>
                </v-row>  
            </v-container>          
        </template>

<!-- Actions + Validation -->
        <template v-slot:actionsWithValidation>
            <v-card-actions  v-if="showUploadActions" class="pt-0 pb-0 pr-6 pl-6">
                    <v-container fluid>
                        <v-form v-model="valid">
                            <v-row>
                                <v-col cols="8">
                                    <v-file-input
                                        v-model="file"
                                        :label="$t('lang_GENERAL_CAPTION_SELECT_FILE')"
                                        :rules="rules"
                                        @change="fileExt = $alvPlugin.extractFileExt(file); countCwrJsonWorks(file)"
                                        @click:clear="resetVal()"
                                        show-size
                                        :alvqa="alvqaContext + '-' + alvqa + '-file-input-import-file'"
                                    >
                                        <template v-slot:append>
                                            <span
                                                v-if="jsonWorksCount !== null"
                                                class="caption grey--text text--darken-1 text-no-wrap"
                                                :alvqa="alvqaContext + '-' + alvqa + '-json-works-count'"
                                            >({{ $t('lang_GENERIC_CAPTION_NUMBER_OF_WORKS') }}:{{ jsonWorksCount }})</span>
                                        </template>
                                    </v-file-input>
                                </v-col>
                                <v-spacer />
                                <v-col 
                                    class="text-right" 
                                    align-self="center"
                                >
                                <BaseButton
                                    :disabled="!valid || !file || !isUploadAllowed"
                                    @click="passes(submitFile)"
                                    :abbreviation="selectedAbbreviationText"
                                    label="lang_GENERAL_CAPTION_UPLOAD"
                                    :alvqa="alvqaContext + '-' + alvqa+'-button-upload'"
                                />    
                                </v-col>
                            </v-row> 
                        </v-form>
                    </v-container>   
            </v-card-actions>
        </template>
    </BaseDialog>
    </ValidationObserver>
</template>

<script>
import api from '@/api_alv'
import BaseDialog from '../_base/BaseDialog'
import BaseButton from '../_buttons/BaseButton'
import BaseIcon from '@/components/_buttons/BaseIcon'
import ContextHelpIcon from '@/components/_buttons/ContextHelpIcon'

export default {
    name: "FileImportDialog",
    components: {
        BaseDialog,
        BaseButton,
        BaseIcon,
        ContextHelpIcon
    },
    props: {
        context: {
            type: String,
            default: ''
        },
        alvqaContext: {
            type: String,
            default: ''
        },
        isUploadAllowed: {
            type: Boolean,
            default: true
        }
    },
    data() {
        return {
            valid: false,
            formData: new FormData(),
            file: null,
            fileExt: '',            
            showUploadProgressbar: false,
            showProcessProgressbar: false,
            showUploadOptions: true,
            showUploadActions: true,
            processValue: 0,
            polling: null,
            dialogTextClass: "",
            rules: [value => !value || this.isFileExtAccepted() || this.$t('lang_GENERAL_DIALOG_TEXT_INVALID_FORMAT') ],
            alvqa: 'file-import-dialog',
            CsvReportPath: null,
            jsonWorksCount: null
        }
    },
    computed: {  
        // Getters & Setters from the Store
        selectedPeriodItem() { return this.$store.getters.getSelectedPeriodTimeUnitImportDialog },
        selectedPeriodNumber() { return this.$store.getters.getSelectedPeriodNumberImportDialog },
        selectedPeriodYear() { return this.$store.getters.getSelectedPeriodYearImportDialog },

        uploadProgressbarValue: {
            get() { return this.$store.getters.getUploadProgressbarValue },
            set(value) { this.$store.commit('setUploadProgressbarValue', value) }
        },

        showFileImportDialog: {
            get() { return this.$store.getters.getShowFileImportDialog },
            set(showFileImportDialog) { this.$store.commit('setShowFileImportDialog', showFileImportDialog) }
        },
        dialogText: {
            get() { return this.$store.getters.getImportDialogText },
            set(text) { this.$store.commit('setImportDialogText', text) }
        },        
        selectedFileImportOption: {
            get() { return this.$store.getters.getSelectedFileImport },
            set(option) { this.$store.commit('setSelectedFileImport', option) }
        },
        getImporterObject() {
            if (this.$route.name === 'SalesData' && this.$store.getters.getSalesDataImportOptions) {
                return this.$store.getters.getSalesDataImportOptions.find(el => el.value == this.selectedFileImportOption)
            }

            return null;
        },
        selectedCheckboxes: { //rename
            get() { return this.$store.getters.getSelectedImportDialogCheckboxes },
            set(options) { this.$store.commit('setSelectedImportDialogCheckboxes', options)}
        },  
        currentAbbreviation() {
            if (this.$route.name === 'Income') {
                return this.$store.getters.getPublishingIncomeDetailsAbbreviation;
            } else if (this.$route.name === 'SalesData') {
                return this.$store.getters.getSalesDataDetailsAbbreviation;
            }
            return null; // Or some default value if necessary
        },
        selectedAbbreviationText() {
            const selectedImport = this.$store.getters.getSelectedFileImport;
            if (selectedImport && this.getImporterObject && this.getImporterObject.mappings) {
                const mapping = this.getImporterObject.mappings.find(m => m.value === this.currentAbbreviation);
                return mapping ? mapping.text : '';
            }
            return '';
        },
        selectedAbbreviationFileExt() {
            const selectedImport = this.$store.getters.getSelectedFileImport;
            if (selectedImport && this.getImporterObject && this.getImporterObject.mappings) {
                const mapping = this.getImporterObject.mappings.find(m => m.value === this.currentAbbreviation);
                return mapping ? mapping.fileFormat : '';
            }
            return '';
        },
        cwrImportType() {
            return this.$store.getters.getWorksDetailsImportCwrType
        }
    },
    watch: {
        showFileImportDialog: {
            immediate: true,
            handler(open) {
                if (open) this.syncCwrJsonDialogText()
            }
        },
        cwrImportType(type, prev) {
            this.syncCwrJsonDialogText(prev)
            if (type === 'cwrJson' && this.file) this.countCwrJsonWorks(this.file)
            else if (prev === 'cwrJson') this.jsonWorksCount = null
            if ((type === 'cwrJson' || prev === 'cwrJson') && this.file) {
                this.rules = [value => !value || this.isFileExtAccepted() || this.$t('lang_GENERAL_DIALOG_TEXT_INVALID_FORMAT')]
            }
        }
    },
    methods: {
        // File upload to server and show the progress of the process or show errors if not successful.
        async submitFile() {
            this.dialogText = this.$t('lang_GENERAL_FILE_UPLOAD_DIALOG_TEXT_FILE_IS_BEING_UPLOADED') 
            this.formData.append('file', this.file)  
            this.showUploadOptions = false          
            this.showUploadActions = false
            this.showUploadProgressbar = true             

            // API Call to upload a file
            let uploadFileResponse = await this.uploadFile()   
            this.showUploadProgressbar = false

            if(uploadFileResponse.error === -1) {
                // Add "successfull dialog text" generic message
                this.dialogText = this.$t('lang_GENERAL_FILE_UPLOAD_DIALOG_TEXT_UPLOAD_SUCCESS')

                if(uploadFileResponse.hasOwnProperty('response')) {
                    // If everything was fine and BE is sending a report message (response > txt), show that message.
                    if(uploadFileResponse.response.txt !== null) {
                        let text = uploadFileResponse.response.txt
                        this.dialogText += '<br /><br />'
                        this.dialogText += this.$alvPlugin.isLangConst(text) ? this.$t(text) : text 
                        this.dialogText += '<br />'
                    }
                }

                if(uploadFileResponse.processId == null) {
                    // Exception: Shipping import shipping rows, showing result and csv file to download.
                    if(this.$route.name === 'ShippingEdit') {
                        let result = uploadFileResponse.txt
                        this.dialogText += '<br /><br />'
                        this.dialogText += this.$t('lang_GENERIC_CAPTION_UPDATED') + ': ' + result.rowsUpdated + ' '
                        this.dialogText += this.$t('lang_GENERIC_CAPTION_ROWS_WITH') + ' ' + result.totalQuantityUpdated + ' ' + this.$t('lang_GENERIC_CAPTION_QUANTITY') + '<br />'
                        this.dialogText += this.$t('lang_GENERIC_CAPTION_ADDED') + ': ' + result.rowsAdded  + ' '
                        this.dialogText += this.$t('lang_GENERIC_CAPTION_ROWS_WITH') + ' ' + result.totalQuantityAdded + ' ' + this.$t('lang_GENERIC_CAPTION_QUANTITY') + '<br />'
                        this.dialogText += this.$t('lang_GENERIC_CAPTION_TOTAL') + ': ' + (result.totalQuantityUpdated + result.totalQuantityAdded) + ' ' + this.$t('lang_GENERIC_CAPTION_QUANTITY') + '<br />'
                    } 

                    // Exception for Edit Shipping + Invoices
                    if(this.$route.name === 'ShippingEdit' || this.$route.name === 'InvoicesEdit') {
                        this.CsvReportPath = uploadFileResponse.pathCsv
                    }

                    // Add "Close dialog text" generic message
                    this.dialogText += '<br />' + this.$t('lang_GENERAL_DIALOG_TEXT_CLOSE_DIALOG')
                    
                    // reload the list
                    if(this.context !== '') this.$store.dispatch('fetch' + this.context)
                    else this.$store.dispatch('fetch' + this.$route.name) 
                    this.showProcessProgressbar = false 
                } else {
                    this.showProcessProgressbar = true  
                    this.dialogText += '<br />' + this.$t('lang_GENERAL_FILE_UPLOAD_DIALOG_TEXT_FILE_IS_BEING_PROCESSED')

                    // API Call to process a file
                    let processFileResponse = await this.processUploadFile(uploadFileResponse)
                    if(processFileResponse.status === 'running') {
                        this.polling = setInterval(() => {
                            this.processUploadFile(uploadFileResponse) }, 5000)
                            setTimeout(() => { clearInterval(this.polling) }, 1800000)                    
                    }               
                }
            } else if (uploadFileResponse.error === 1) {
                let errorMsg = uploadFileResponse.response.errorMsg
                let pathCsv = uploadFileResponse.response.path
                if (pathCsv) this.CsvReportPath = pathCsv
                this.dialogText = this.resolveImportErrorMessage(errorMsg)
                this.dialogText += '<br /><br />' + this.$t('lang_GENERAL_DIALOG_TEXT_CLOSE_DIALOG')
                // reload the list
                if(this.context !== '') this.$store.dispatch('fetch' + this.context)
                else this.$store.dispatch('fetch' + this.$route.name)  
            }    
        },

        /** Calls the api function to upload a file.
         * 
         *  @return {{}} Info-Messages and fileId. */
        async uploadFile() {
            let dir = this.$route.path.slice(1)
            let params = '?type=' + this.$store.getters.getSelectedFileImport
            if(this.$store.getters.getSelectedFileImport.value){
                params = '?type=' + this.$store.getters.getSelectedFileImport.value
            }

            let result  

            // check which page want to upload/import a file
            switch(this.$route.name) {
                case 'Income': 
                // check if period is selected
                    params += '&' + this.$alvPlugin.buildPeriodStringForApiCall(this.selectedPeriodItem, this.selectedPeriodYear, this.selectedPeriodNumber)

                // check if checkbox for "run error lists" is selected.
                    if(this.selectedCheckboxes.includes('runErrorLists')) params += '&runErrorLists=yes'
                    else params += '&runErrorLists=no'

                // check if "abbreviation" is selected
                    let abbreviation = this.$store.getters.getPublishingIncomeDetailsAbbreviation
                    if(abbreviation !== null && abbreviation !== undefined) {
                        params += '&abbreviation=' + abbreviation
                    }

                // check if checkbox for "without assigning" is selected.
                    if(this.selectedCheckboxes.includes('withoutAssigning')) params += '&withoutAssigning=yes'
                    else params += '&withoutAssigning=no'

                    let commPercent = this.$store.getters.getPublishingIncomeDetailsImportCommissionPercent
                    if(commPercent !== null) {
                        params += '&commissionPercent=' + commPercent
                    }

                    let incomeCcf = this.$store.getters.getPublishingIncomeDetailsImportCurrencyConversionFactor
                    if(incomeCcf) {
                        params += '&conversionFactor=' + incomeCcf
                    }

                    result = await api.putFile(dir, this.formData, params)
                    break
                case 'Works':                   
                    if(this.$store.getters.getSelectedFileImport == 'alvExcelTemplateWorks') params = "?type=alvExcel"
                    else if (this.$store.getters.getSelectedFileImport == 'cwr' && this.cwrImportType === 'cwrJson') {
                        params = this.cwrJsonImportParams()
                    }
                    else if (this.$store.getters.getSelectedFileImport == 'cwr') {
                        let cwrType = this.$store.getters.getWorksDetailsImportCwrType
                        let cwrSourceShareType = this.$store.getters.getWorksDetailsImportCwrSourceShareType
                        let cwrTargetShareType = this.$store.getters.getWorksDetailsImportCwrTargetShareType
                        let cwrSourcePublisherAz = this.$store.getters.getWorksDetailsCwrPublisherSourceAz  ? this.$store.getters.getWorksDetailsCwrPublisherSourceAz.alvId : null
                        params += `&cwrType=${cwrType}&cwrSourceShares=${cwrSourceShareType}&cwrTargetShares=${cwrTargetShareType}&cwrSourcePublisherAz=${cwrSourcePublisherAz}`
                    }
                    else if (this.$store.getters.getSelectedFileImport == 'mappingImport') {
                        let mappingAlvId = this.$store.getters.getWorksDetailsImportSelectedMapping
                        params += `&mappingAlvId=${mappingAlvId}`
                        let searchByTitleAndComposers = this.$store.getters.getWorksDetailsSearchWorkByTitleCheckbox
                        params += `&searchWorkTitle=${searchByTitleAndComposers ? 'yes' : 'no'}`
                    }
                    else if (this.$store.getters.getSelectedFileImport == 'cwrJson') {
                        params = this.cwrJsonImportParams()
                    }
                    if(this.$store.getters.getWorkImportTags !== null) params += '&tag=' + this.$store.getters.getWorkImportTags
                    result = await api.putFile(dir, this.formData, params)
                    break
                case 'SalesData':
                    let importer = this.getImporterObject
                    let ccf = this.$store.getters.getSalesDataDetailsImportCurrencyConversionFactor
                    if(ccf) {
                        params += '&conversionFactor=' + ccf
                    }

                    // check if "abbreviation" is selected
                    let salesDataAbbreviation = this.$store.getters.getSalesDataDetailsAbbreviation
                    if(salesDataAbbreviation !== null && salesDataAbbreviation !== undefined) {
                        params += '&abbreviation=' + salesDataAbbreviation
                    }

                    // check if period is selected
                    params += '&' + this.$alvPlugin.buildPeriodStringForApiCall(this.selectedPeriodItem, this.selectedPeriodYear, this.selectedPeriodNumber)             
                    if(this.selectedCheckboxes.includes('runErrorLists')) params += '&runErrorLists=yes'
                    else params += '&runErrorLists=no'

                    // check if "ImportCommissionPercent" field is populated , cp - commission percent
                    let cp = this.$store.getters.getSalesDataDetailsImportCommissionPercent
                    if(cp !== null) {
                        params += '&commissionPercent=' + cp
                    }

                    result = await api.putFile(dir, this.formData, params)
                    break
                case 'PublishingProjects':
                    if(this.$store.getters.getSelectedFileImport == 'alvExcelTemplatePublishingProjects') params = "?type=alvExcel"
                    else if (this.$store.getters.getSelectedFileImport == 'mappingImport') {
                        let mappingAlvId = this.$store.getters.getPublishingProjectsDetailsImportSelectedMapping
                        params += `&mappingAlvId=${mappingAlvId}`

                        if (this.$store.getters.getPublishingProjectsDetailsImportTags) {
                            params += `&tag=${this.$store.getters.getPublishingProjectsDetailsImportTags}`
                        }
                    }

                    result = await api.putFile('publishing/releases', this.formData, params)
                    break    
                case 'Tracking':     
                    params += "&year=" + this.$store.getters.getSelectedUploadYear
                    
                    result = await api.putFile('publishing/tracking/prolists', this.formData, params)                    
                    break
                case 'LabelReleases':
                    if (this.$store.getters.getSelectedFileImport == 'mappingImport') {
                        let mappingAlvId = this.$store.getters.getLabelReleasesDetailsImportSelectedMapping
                        params += `&mappingAlvId=${mappingAlvId}`

                        if (this.$store.getters.getLabelReleasesDetailsImportTags) {
                            params += `&tag=${this.$store.getters.getLabelReleasesDetailsImportTags}`
                        }
                    }
                    
                    result = await api.putFile('label/releases', this.formData, params)  
                    break
                case 'Tracks':
                    // Label Tracks supports upload only through mapping right now
                    if (this.$store.getters.getSelectedFileImport == 'mappingImport') {
                        let mappingAlvId = this.$store.getters.getTracksDetailsImportSelectedMapping
                        params += `&mappingAlvId=${mappingAlvId}`

                        if (this.$store.getters.getTracksDetailsImportTags) {
                            params += `&tag=${this.$store.getters.getTracksDetailsImportTags}`
                        }

                        result = await api.putFile('label/tracks', this.formData, params)
                    }
                    break
                case 'AzPersons':
                    if (this.$store.getters.getSelectedFileImport == 'mappingImport') {
                        let mappingAlvId = this.$store.getters.getAzPersonsDetailsImportSelectedMapping
                        params += `&mappingAlvId=${mappingAlvId}`

                        if (this.$store.getters.getAzPersonsDetailsImportTags) {
                            params += `&tag=${this.$store.getters.getLabelReleasesDetailsImportTags}`
                        }
                    }
                    result = await api.putFile('az/persons', this.formData, params)  
                    break
                case 'LabelContracts':
                    result = await api.putFile('contracts/label', this.formData, params)  
                    break
            // Shipping
                case 'ShippingEdit':
                    let alvId = this.$store.getters.getShippingDetailsAlvId
                    this.$store.dispatch('saveShippingForm')
                    result = await api.putFile('erp/shipping/' + alvId, this.formData)
                    break
            // Invoices
                case 'InvoicesEdit':
                    let invoicesAlvId = this.$store.getters.getInvoicesDetailsAlvId
                    this.$store.dispatch('saveInvoicesForm')
                    result = await api.putFile('erp/invoices/' + invoicesAlvId, this.formData)
                    break
                default: 
                    result = {
                        error: 1, 
                        response: {
                            txt: this.$t("lang_FILE_UPLOAD_ERROR"),
                            errorMsg: this.$t("lang_FILE_UPLOAD_ERROR")
                        }
                    } 
            }          
            return result
        },

        /** Calls the api function to process a uploaded file.
         * 
         * @return {{}} */        
        async processUploadFile(uploadResponse) {
            let dir = this.$route.path.slice(1)
            let result = await api.getProgressOfProcess(uploadResponse.processId, this.polling).then(response => { return response })
            this.processValue = result.percent

            if(result.status === 'finished') {
                clearInterval(this.polling) 

                if(result.error === 1) {
                    this.showProcessProgressbar = false
                    if (result.path) this.CsvReportPath = result.path
                    if (result.dialogCode) {
                        this.dialogText = this.$alvPlugin.buildDialogMessage(result.dialogCode, result.dialogParameters)
                    } else {
                        this.dialogText =   this.resolveImportErrorMessage(result.errorMsg) +
                                            '<br /><br />' + this.$t('lang_GENERAL_DIALOG_TEXT_CLOSE_DIALOG')
                    }
                } else {
                    // Label > Release > Show summary at the end, so that the user can see what was imported.
                    if(this.$route.name === "LabelReleases" && this.$store.getters.getSelectedFileImport == 'mappingImport') {
                        this.dialogText = result.txt + this.$t('lang_GENERAL_DIALOG_TEXT_CLOSE_DIALOG')
                    }
                    else {
                        this.dialogText = ""
                        if (result.completeMsgArray.txt && this.$route.name === 'Works') {
                            let text = result.completeMsgArray.txt
                            this.dialogText += (this.$alvPlugin.isLangConst(text) ? this.$t(text) : text) + '<br />'
                        }

                        this.dialogText += this.$t('lang_GENERAL_FILE_UPLOAD_DIALOG_TEXT_PROCESSING_SUCCESS') + '<br />' + this.$t('lang_GENERAL_DIALOG_TEXT_CLOSE_DIALOG')
                        if (result.path != undefined && result.path != null && result.path != "")
                            this.dialogText += '<br /><br /><a href="' + result.path + '" target="_blank">' + this.$t('lang_GENERIC_CAPTION_DOWNLOAD_CSV_REPORT') + '</a>'
                    }
                }

                // reload the list
                if(this.context !== '') this.$store.dispatch('fetch' + this.context)
                else this.$store.dispatch('fetch' + this.$route.name) 
            }
            return result
        },

        // Checks if file is accepted, depends on chosen Import Option.
        isFileExtAccepted() {
            // importer API call returns list of importers with file extensions written as "xlsx|xls" for example
            // limit to Label > Sales data
            if(this.$route.name === "SalesData") {
                let abbreviation = this.currentAbbreviation
                let arrayOfFileExtensions = this.$store.getters.getSalesDataDetailsFileExtensions.split("|");
                
                if (this.fileExt.toLowerCase() == "zip") return true; // always accept ZIP files, BE fill check containing file on its end
                if (abbreviation && this.selectedAbbreviationFileExt) {
                    arrayOfFileExtensions = [this.selectedAbbreviationFileExt]
                }
                
                return arrayOfFileExtensions.includes(this.fileExt)
            }
            if(this.$route.name === "Income") {

                const abbreviation = this.$store.getters.getIncomeInlineEditingDialogDetails.abbreviation
                const selectedFileImportOption = this.$store.getters.getSelectedFileImportOption

                if (this.fileExt.toLowerCase() == "zip") return true; // always accept ZIP files, BE fill check containing file on its end

                if(abbreviation) {
                    const fmt = selectedFileImportOption.mappings.find(mapping=>mapping.value === abbreviation).fileFormat.toLowerCase()
                    return fmt === this.fileExt.toLowerCase() || (fmt === 'csv' && this.fileExt.toLowerCase() === 'txt')
                }
                return !!selectedFileImportOption.fileExtensions.split(';').find(fileFormat=>fileFormat.replaceAll(';','').toLowerCase() === this.fileExt.toLowerCase() || (fileFormat.replaceAll(';','').toLowerCase() === 'csv' && this.fileExt.toLowerCase() === 'txt'))
            }
            // for legacy importers
            switch(this.selectedFileImportOption) {
// XLSX + XLS
                case 'alvExcelTemplateWorks': case 'svd': case 'setlistXlsx': case 'alvExcelTemplatePublishingProjects':
                case 'alvExcelTemplateLabelReleases': case 'alvExcelTemplateLabelSalesData': case 'allegro': 
                case 'finetunes': case 'galileo': case 'damusic': case 'hart': case 'naxosusa': case 'outside':
                case 'pias': case 'planet': case 'qedovaloffice': case 'roughtrade': case 'sony': case 'soulfood': 
                case 'telamomusik': case 'warnermusic': case 'wordandsound': case 'a_tempo': case 'universalmusicentertainment': 
                case 'alvExcelTemplateLabelContracts': case 'alvExcelTemplateAzPersons': case 'suisaWorks':

                    return this.fileExt === 'xlsx' || this.fileExt === 'xls'
// v21 or v22
                case 'cwr':
                    if (this.cwrImportType === 'cwrJson') return this.fileExt === 'json'
                    let extension = this.fileExt.toUpperCase() 
                    return extension === 'V21' || extension === 'V22'
                case 'cwrJson':
                    return this.fileExt === 'json'
// Mapping import
                case 'mappingImport':
                    let routes = ['Works', 'LabelReleases', 'Tracks', 'AzPersons', 'PublishingProjects']
                    
                    if (routes.includes(this.$route.name)) {
                        let selectedMapping = null
                        switch(this.$route.name) {
                            case 'Works':
                                selectedMapping = this.$store.getters.getWorksDetailsImportSelectedMapping;
                                break;
                            case 'LabelReleases':
                                selectedMapping = this.$store.getters.getLabelReleasesDetailsImportSelectedMapping;
                                break;
                            case 'Tracks':
                                selectedMapping = this.$store.getters.getTracksDetailsImportSelectedMapping;
                                break;
                            case 'AzPersons':
                                selectedMapping = this.$store.getters.getAzPersonsDetailsImportSelectedMapping;
                                break;
                            case 'PublishingProjects':
                                selectedMapping = this.$store.getters.getPublishingProjectsDetailsImportSelectedMapping;
                                break;
                        }
                        let mappings = this.$store.getters.getMappings ? this.$store.getters.getMappings : []

                        let mappingObject = mappings.find((v) => v.alvId == selectedMapping)
                        if (mappingObject === undefined) return false
                        const fmt = mappingObject.fileFormat.toLowerCase()
                        return fmt === this.fileExt.toLowerCase() || (fmt === 'csv' && this.fileExt === 'txt')
                    }
// ZIP
                case 'fuga': case 'theorchard': case 'gemaNA': case 'setlistZip': 
                case 'alvZipLabelReleases': case 'musicreports':
                    return this.fileExt === 'zip'
// CSV
                case 'awalrecordings': case 'bandcamp': case 'brokensilence': case 'feiyr': case 'maestroAt':
                case 'kontor': case 'recordjet': case 'zebralution': case 'edel': case 'bmgus': case 'bmgde': case 'igroove': case 'harvestMedia':
                    return this.fileExt === 'csv'
// CSV + ZIP
                case 'believedigital': case 'editionbjorlund': case 'bmgusa': case 'roba': 
                    return this.fileExt === 'csv' || this.fileExt === 'zip'
// XLSX
                case 'naxos': case 'spectre': case 'shippingRows': case 'invoiceRows': case 'adaworldwide': case 'ygplus':
                    return this.fileExt === 'xlsx'
// CSV + XLSX
                case 'soulfoodbelieve':
                    return this.fileExt === 'csv' || this.fileExt === 'xls'
// GDF + ZIP
                case 'gdf': 
                    return this.fileExt === 'gdf' || this.fileExt === 'zip'
// TXT + CSV + ZIP
                case 'akm': case 'maestroEurope':
                    return this.fileExt === 'txt' || this.fileExt === 'csv' || this.fileExt === 'zip'
// CSV + TXT
                case 'aume':
                    return this.fileExt === 'csv' || this.fileExt === 'txt'
// TXT + ZIP
                // case 'maestroItaly': case 'maestroAtSv': case 'maestroCh':
                case 'ctmpublishing': case 'iqmusic': 
                    return this.fileExt === 'txt' || this.fileExt === 'zip'
// ZIP + F + E3 + M2
                case 'suisaIncome':
                    return this.fileExt === 'zip' || this.fileExt === 'f' || this.fileExt === 'e3' || this.fileExt === 'm2'
// CSV + XLSX + XLS 
                case 'indigo':
                    return this.fileExt === 'xlsx' || this.fileExt === 'xls' || this.fileExt === 'csv'
            }
        }, 
        cwrJsonImportParams() {
            const cwrSourcePublisherAz = this.$store.getters.getWorksDetailsCwrPublisherSourceAz
                ? this.$store.getters.getWorksDetailsCwrPublisherSourceAz.alvId
                : null
            const cwrSourceShareType = this.$store.getters.getWorksDetailsImportCwrSourceShareType
            const cwrTargetShareType = this.$store.getters.getWorksDetailsImportCwrTargetShareType
            const schema = this.$store.getters.getWorksImportCwrJsonDetailsSchema
            const includes = this.$store.getters.getWorksImportCwrJsonDetailsInclude
            const excludes = this.$store.getters.getWorksImportCwrJsonDetailsExclude
            let params = `?type=cwrJson&schema=${schema}&cwrSourcePublisherAz=${cwrSourcePublisherAz}&cwrSourceShares=${cwrSourceShareType}&cwrTargetShares=${cwrTargetShareType}`
            if (includes?.length) params += `&regionsInclude=${includes.join(',')}`
            if (excludes?.length) params += `&regionsExclude=${excludes.join(',')}`
            return params
        },

        async countCwrJsonWorks(file) {
            this.jsonWorksCount = null
            if (!file || this.$route.name !== 'Works' || this.cwrImportType !== 'cwrJson') return
            if (this.selectedFileImportOption !== 'cwr' && this.selectedFileImportOption !== 'cwrJson') return
            if ((this.$alvPlugin.extractFileExt(file) || '').toLowerCase() !== 'json') return
            try {
                const text = await file.text()
                const data = JSON.parse(text)
                this.jsonWorksCount = data && Array.isArray(data.works) ? data.works.length : null
            } catch (e) {
                this.jsonWorksCount = null
            }
        },

        syncCwrJsonDialogText(prevType) {
            if (this.$route.name !== 'Works') return
            if (this.selectedFileImportOption !== 'cwr') return
            if (!this.showFileImportDialog || !this.showUploadOptions) return
            if (this.cwrImportType === 'cwrJson') this.dialogText = this.importDialogText('JSON')
            else if (prevType === 'cwrJson') this.dialogText = this.importDialogText('CWR')
        },

        importDialogText(kind) {
            const tempText = this.$t('lang_GENERAL_IMPORT_DIALOG_TEXT_WANT_TO_UPLOAD') + ' fileType' + this.$t('lang_GENERIC_CAPTION_FILE') + '. <br />' +
                this.$t('lang_GENERAL_IMPORT_DIALOG_TEXT_SELECT_FILE_TYPE')
            const fileType = kind === 'JSON'
                ? this.$t('lang_CONTRACTS_PUBLISHING_WORKSEXPORT_CAPTION_TYPE_JSON')
                : 'CWR'
            const join = kind === 'JSON' ? 'JSON' : this.$t('lang_PUBLISHING_WORKS_CAPTION_IMPORT_CWR_V21_V22')
            return tempText.replace(/fileType|<join>/gi, matched => {
                return { fileType, '<join>': join }[matched]
            })
        },

        async resetVal() {
            await this.$nextTick()
            this.file = null
            this.jsonWorksCount = null
        },

        downloadFile(path) {
            api.getFileFromALV(path)
        },

        // Close the FileImportDialog and sets back to default.
        closeDialog() {
            this.showFileImportDialog = false         
            this.showUploadProgressbar = false
            this.showProcessProgressbar = false
            this.showUploadOptions = true
            this.showUploadActions = true
            this.CsvReportPath = null
            this.file = null
            this.formData = new FormData()
            this.processValue = 0
            this.$store.commit('setSelectedPeriodValuesImportDialog', {
                num: null,
                period: "notDefined",
                year: null
            })
            this.uploadProgressbarValue = 0
            this.selectedFileImportOption = []
            this.dialogTextClass = ""
            this.selectedCheckboxes = ["runErrorLists"]
            
            if(this.$route.name === "SalesData") {
                this.$store.commit('setSalesDataDetailsImportCurrencyConversionFactor', null)
                this.$store.commit('setSalesDataDetailsImportCommissionPercent', null)
                this.$store.commit('setSalesDataDetailsFileExtensions', null)
                this.$store.commit('setSalesDataDetailsAbbreviation', [])
            }
            if(this.$route.name === "Income") {
                this.$store.commit("setPublishingIncomeDetailsImportCurrencyConversionFactor", null)
                this.$store.commit("setPublishingIncomeDetailsImportCommissionPercent", null)
            }
            if(this.$route.name === "PublishingProjects") {
                this.$store.commit('setPublishingProjectsDetailsImportTags', "")
                this.$store.commit('setPublishingProjectsDetailsImportSelectedMapping', null)
            }
            if(this.$route.name === "Works") {
                this.$store.commit('setWorkImportTags', null)
                this.$store.commit('setWorksDetailsCwrPublisherSourceAz', null)
                this.$store.commit('setWorksCwrPublisherSourceAzOptions', [])
                this.$store.commit('setWorksImportCwrJsonDetailsSchema', 'cwr-json-v1.0')
                this.$store.commit('setWorksImportCwrJsonDetailsInclude', [])
                this.$store.commit('setWorksImportCwrJsonDetailsExclude', [])
                this.jsonWorksCount = null
                clearInterval(this.polling)
            }
        },

        resolveImportErrorMessage(errorMsg) {
            if (
                errorMsg === 'ALVID_WRONG_PAYLOAD' &&
                (this.$store.getters.getSelectedFileImport === 'cwrJson' ||
                    (this.$store.getters.getSelectedFileImport === 'cwr' && this.cwrImportType === 'cwrJson'))
            ) {
                return this.$t('lang_GENERAL_VALIDATION_MESSAGE_FIELD_REQUIRED', {
                    _field_: this.$t('lang_PUBLISHING_WORKS_CAPTION_FROM_PUBLISHER')
                })
            }
            return this.$alvPlugin.isLangConst(errorMsg) ? this.$t(errorMsg) : errorMsg
        },
    },
    beforeDestroy () {
	    clearInterval(this.polling)
    },
    mounted() {
        this.$store.commit('setSalesDataImportOptions')
    }
}
</script>

<style scoped>
.black-label {
    color: black;
}
</style>